import { logger } from '@/utils/logger/logger';
import { appendQueryParam } from '@heidi/api/config';
import { WORKSPACE_ID_QUERY_PARAM, getWorkspaceId } from '@heidi/workspace/data';

// `instanceof Error` is false for a cross-realm throw, and `new URL()` is one
// under jsdom, so read the name off the shape instead of the prototype. Never
// the message: it embeds the URL, which carries the auth token.
const errorNameOf = (error: unknown): string =>
  typeof error === 'object' &&
  error !== null &&
  typeof Reflect.get(error, 'name') === 'string'
    ? String(Reflect.get(error, 'name'))
    : typeof error;

/**
 * Appends the active workspace to a session WebSocket URL. A browser cannot set
 * `Heidi-Workspace-Id` on an upgrade, so these sockets carry it as a query
 * param. No active workspace appends nothing, and the transcribe-live and
 * dictate routes then leave the connection unpinned rather than inferring a
 * default — unlike the voice socket, which does infer one.
 *
 * Returns the URL untouched on failure: a websocket origin that is not an
 * absolute URL is a deployment misconfiguration, and it must not turn a
 * recording or dictation start into a thrown error.
 */
export const appendWorkspaceIdParam = (url: string): string => {
  try {
    return appendQueryParam(url, WORKSPACE_ID_QUERY_PARAM, getWorkspaceId());
  } catch (error) {
    logger.error({
      event_type: 'WORKSPACE_WEBSOCKET_PARAM_ERROR',
      details: { errorName: errorNameOf(error) },
    });
    return url;
  }
};
